import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { reduce } from 'lodash';
import { required, validate } from '../../shared';
import { ErrorResponse } from './error-response';
import { StatusCode } from './status-code';
import { StatusCodeGroup } from './status-code-group';
import { coreConfig } from '../core.config';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  @validate
  handleError(@required error: HttpErrorResponse): ErrorResponse {
    let result: ErrorResponse;
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred.
      result = new ErrorResponse({
        status: StatusCode.Unknown,
        message: error.error.message,
      });
    } else {
      // The backend returned an unsuccessful response code.
      const serverError: any = error.error || {};
      result = this.mapErrorResponse(serverError, error.status);
    }

    return result;
  }

  mapErrorResponse(response: any, status: number = StatusCode.Unknown): ErrorResponse {
    let error: ErrorResponse;
    if (!response) {
      error = new ErrorResponse({ status: status });
    } else {
      error = new ErrorResponse({
        status: status,
        code: response.code,
        error: response.error,
        message: response.message,
        details: response.details,
      });
    }

    return error;
  }

  @validate
  mapDataToFormData<T extends object>(@required data: T): FormData {
    const formData = reduce(
      data,
      (result: FormData, value: any, key: string) => {
        result.append(key, value);
        return result;
      },
      new FormData()
    );

    return formData;
  }

  @validate
  setNoCacheQueryParam(@required url: string, noCacheValue: string = new Date().getTime().toString()): string {
    const separator = url.indexOf('?') === -1 ? '?' : '&';

    const urlWithNoCacheQueryParam = `${url}${separator}${coreConfig.api.params.noCache}=${encodeURI(noCacheValue)}`;

    return urlWithNoCacheQueryParam;
  }

  getStatusCodeGroup(statusCode: number): StatusCodeGroup {
    if (statusCode >= 100 && statusCode < 200) {
      return StatusCodeGroup.Information;
    } else if (statusCode >= 200 && statusCode < 300) {
      return StatusCodeGroup.Success;
    } else if (statusCode >= 300 && statusCode < 400) {
      return StatusCodeGroup.Redirection;
    } else if (statusCode >= 400 && statusCode < 500) {
      return StatusCodeGroup.ClientError;
    } else if (statusCode >= 500 && statusCode < 600) {
      return StatusCodeGroup.ClientError;
    } else {
      return StatusCodeGroup.Unknown;
    }
  }
}
